/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useEffect, useCallback, ChangeEventHandler, ChangeEvent, useReducer, useMemo } from 'react';
import debounce from 'lodash/debounce';

import SearchField from './components/SearchField';
import Suggestions from './components/Suggestions';
import Results from './components/Results';

import { SearchResult } from './api/services/search/interfaces';
import SearchApiService from './api/services/search/searchApiService';

interface Props {
  locale: string;
}

type State = {
  loading: boolean;
  open: boolean;
  searchResults: SearchResult[] | undefined;
  searchTerm: string;
};

type Action =
  | {
      type: 'change-search-term';
      searchTerm: string;
    }
  | { type: 'open-search' }
  | {
      type: 'reset-search';
    }
  | {
      type: 'start-search';
    }
  | {
      type: 'results-loaded';
      searchResult: SearchResult[];
    };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'change-search-term':
      return {
        ...state,
        loading: true,
        searchTerm: action.searchTerm,
      };
    case 'open-search':
      return {
        ...state,
        open: true,
      };
    case 'reset-search':
      return {
        ...state,
        searchTerm: '',
        loading: false,
        searchResults: [],
        open: false,
      };
    case 'start-search':
      return {
        ...state,
        loading: true,
      };
    case 'results-loaded':
      return {
        ...state,
        loading: false,
        searchResults: action.searchResult,
      };
  }
}

const changeQueryParameter = (searchTerm: string) => {
  const newURL = new URL(window.location.href);
  newURL.searchParams.set('search', searchTerm);
  window.history.replaceState(window.history.state, '', newURL.href);
};

const resetQueryParameter = () => {
  const newURL = new URL(window.location.href);
  newURL.searchParams.delete('search');
  window.history.replaceState(window.history.state, '', newURL.href);
};

const App: React.FC<Props> = ({ locale }: Props) => {
  // determine initial state, respecting searchTerm from query param
  const initialState: State = useMemo(() => {
    const url = new URL(window.location.href);
    const searchTerm = url.searchParams.get('search');

    return { searchResults: undefined, searchTerm: searchTerm || '', loading: false, open: url.hash === '#search' };
  }, []);

  const [state, dispatch] = useReducer(reducer, initialState);

  // resetSearch clears the search field, resets results and clears the query param
  const resetSearch = useCallback(() => {
    dispatch({ type: 'reset-search' });
    resetQueryParameter();
  }, [dispatch]);

  // Trigger an initial search (when we get a search term via query params)
  useEffect(() => doSearch(state.searchTerm), []);

  // effect to open search on open click
  useEffect(() => {
    const elements = Array.from(document.getElementsByClassName('open-search'));
    const listener = () => dispatch({ type: 'open-search' });
    elements.forEach((element) => {
      element.addEventListener('click', listener);
    });

    return () => {
      elements.forEach((element) => element && element.removeEventListener('click', listener));
    };
  });

  // effect to reset search on closing
  useEffect(() => {
    const resetSearchOnEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        resetSearch();
      }
    };

    window.addEventListener('keydown', resetSearchOnEsc);
    const element = document.getElementsByClassName('close-search').item(0);
    if (element) {
      element.addEventListener('click', resetSearch);
    }
    return () => {
      window.removeEventListener('keydown', resetSearchOnEsc);
      if (element) {
        element.removeEventListener('click', resetSearch);
      }
    };
  }, []);

  // Keep track of the current search term in a ref so we can use it later to prevent race conditions
  const currentSearchTerm = useRef<string | null>(state.searchTerm || null);
  currentSearchTerm.current = state.searchTerm;

  const doSearch = useCallback((searchTerm: string) => {
    dispatch({ type: 'start-search' });

    (async function doSearch() {
      const companyId = window.Liferay.ThemeDisplay.getCompanyId();
      const groupId = window.Liferay.ThemeDisplay.getScopeGroupId();

      try {
        // In a first step the query parameter must be adjusted
        changeQueryParameter(searchTerm);

        if (searchTerm.length > 0) {
          // The search is then performed with the search term
          const searchResults = await SearchApiService.getResults(locale, companyId, groupId, searchTerm);

          // when we got a new search value in the meantime we abort the processing of the results
          if (currentSearchTerm.current !== searchTerm) {
            return;
          }

          dispatch({ type: 'results-loaded', searchResult: searchResults.docs });
        } else {
          resetSearch();
        }
      } catch {
        dispatch({ type: 'results-loaded', searchResult: [] });
      }
    })();
  }, []);

  const debouncedSearch = useMemo(() => debounce(doSearch, 300), [doSearch]);

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: 'change-search-term', searchTerm: e.target.value });
      debouncedSearch(e.target.value);
    },
    [dispatch],
  );

  return (
    <>
      <SearchField
        onChange={handleChange}
        hitCounts={state.searchResults?.length}
        searchValue={state.searchTerm}
        resultsLoading={state.loading}
        searchOpen={state.open}
      />
      {!state.searchResults?.length && <Suggestions />}
      {!state.loading && state.searchResults && <Results results={state.searchResults} />}
    </>
  );
};

export default App;
